// What is live and how far the branch is ahead of it (spec/dashboard.md section 3.3, the release strip under the branch
// selector; section 12.2, releases and pins). One cached read per game: each environment's active default pin and its
// release; per branch: the environments running it, the documents changed between each pinned release and the head
// (`compare` without content, cached per pair of commits), and the branch's latest release.
import { useMemo } from 'react';
import { ApiError } from '../api/client';
import { ops } from '../api/operations';
import type { Environment, Release, ReleasePin, TreeChange } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { queryCache, useQuery } from '../workspace/cache';
import { useGame } from './GameContext';

/** The release an environment's default pin serves. */
export interface PinnedRelease {
  number: number;
  releaseId: string;
  branchName: string;
  commit: string;
  createdAt: string | null;
  builtBy: string | null;
}

export interface EnvReleaseStatus {
  env: Environment;
  /** the active default pin, or null when nothing is pinned */
  pin: { id: string; createdAt: string | null; pinnedBy: string | null; reason: string | null } | null;
  release: PinnedRelease | null;
  /** the caller may not read this environment's pins */
  forbidden: boolean;
}

/** What changed between two commits: documents (paths under `documents/`), the other files, and both. */
export interface ChangeCount {
  documents: number;
  other: number;
  files: number;
}

/** Counts a compare answer's changes: the paths under `documents/` are documents, the rest "other". */
export function countChangedDocuments(changes: readonly Pick<TreeChange, 'path'>[] | null | undefined): ChangeCount {
  let documents = 0;
  let other = 0;
  for (const c of changes ?? []) {
    if (typeof c.path === 'string' && c.path.startsWith('documents/')) documents += 1;
    else other += 1;
  }
  return { documents, other, files: documents + other };
}

/** "3 documents changed since", or the files when no document changed. */
export function changedSinceText(c: ChangeCount): string {
  if (c.documents > 0) return `${c.documents} document${c.documents === 1 ? '' : 's'} changed since`;
  return `${c.files} file${c.files === 1 ? '' : 's'} changed since`;
}

const ZERO: ChangeCount = { documents: 0, other: 0, files: 0 };

/** The cache key of a game's release status (every key of the strip and the game home starts with it). */
export function releaseStatusKey(org: string, game: string): string {
  return `game:${org}/${game}:release-status`;
}

/** Re-reads the release status of a game (after a pin, a revoke, a rollback, a build) and the selector's chips. */
export function invalidateReleaseStatus(org: string, game: string): void {
  queryCache.invalidate(releaseStatusKey(org, game));
  queryCache.invalidate(`game:${org}/${game}:pins`);
}

const actorLabel = (a: unknown): string | null => {
  const x = a as { label?: string | null; id?: string | null } | null | undefined;
  return x?.label ?? x?.id ?? null;
};

const isForbidden = (e: unknown) => e instanceof ApiError && e.status === 403;

function toPinned(r: Release): PinnedRelease {
  return { number: Number(r.number), releaseId: r.releaseId, branchName: r.branchName, commit: r.commit, createdAt: r.createdAt ?? null, builtBy: actorLabel(r.builtBy) };
}

export interface ReleaseStatusState {
  envs: EnvReleaseStatus[] | undefined;
  loading: boolean;
  /** no environment's pins could be read (a 403 everywhere): render nothing */
  forbidden: boolean;
}

/** Every environment of the game with its active default pin and that pin's release; cached per game. */
export function useReleaseStatus(): ReleaseStatusState {
  const { org, game, environments } = useGame();
  const { client } = useAuth();
  const q = useQuery(environments.length ? releaseStatusKey(org, game.slug) : null, () =>
    Promise.all(
      environments.map(async (env): Promise<EnvReleaseStatus> => {
        let pins: ReleasePin[];
        try {
          const page = await client.data(ops.listPins, { path: { org, game: game.slug, env: env.slug }, query: { active: 'true', limit: 50 } });
          pins = page.items as ReleasePin[];
        } catch (e) {
          return { env, pin: null, release: null, forbidden: isForbidden(e) };
        }
        const active = pins.find((p) => p.isDefault && !p.revokedAt && !p.supersededById);
        if (!active) return { env, pin: null, release: null, forbidden: false };
        const pin = { id: active.id, createdAt: active.createdAt ?? null, pinnedBy: actorLabel(active.pinnedBy), reason: active.reason ?? null };
        try {
          // the release by id, under the build dialog's key so the two share one read
          const release = await queryCache.get(`game:${org}/${game.slug}:release:${active.releaseId}`, () => client.data(ops.getRelease, { path: { org, game: game.slug, release: active.releaseId } }) as unknown as Promise<Release>);
          return { env, pin, release: toPinned(release), forbidden: false };
        } catch {
          return { env, pin, release: null, forbidden: false };
        }
      }),
    ),
  );
  const forbidden = !!q.data && q.data.length > 0 && q.data.every((e) => e.forbidden);
  return { envs: q.data, loading: q.loading && !q.data, forbidden };
}

export interface BranchEnvStatus extends EnvReleaseStatus {
  release: PinnedRelease;
  /** what changed between the pinned release's commit and the head; null while unknown */
  changed: ChangeCount | null;
}

export interface BranchReleaseStatus {
  loading: boolean;
  forbidden: boolean;
  head: string | null;
  /** the environments whose default pin was built from this branch */
  environments: BranchEnvStatus[];
  /** the branch's newest release, whether any environment serves it, and what changed since it */
  latest: { release: PinnedRelease; pinned: boolean; changed: ChangeCount | null } | null;
}

/**
 * The release status of one branch: the environments running it, each with what changed since its pinned release, and the
 * branch's latest release. `head` defaults to the branch's head in the game context; a head move re-reads the counts.
 */
export function useBranchReleaseStatus(branch: string | null, head?: string | null): BranchReleaseStatus {
  const { org, game, branches } = useGame();
  const { client } = useAuth();
  const status = useReleaseStatus();
  const h = head ?? branches.find((b) => b.name === branch)?.head ?? null;
  const base = releaseStatusKey(org, game.slug);

  const latestQ = useQuery(
    branch && !status.forbidden ? `${base}:latest:${branch}` : null,
    async () => {
      const page = await client.data(ops.listReleases, { path: { org, game: game.slug }, query: { branch: branch!, limit: 1 } });
      const r = (page.items as Release[])[0];
      return r ? toPinned(r) : null;
    },
    { head: h },
  );

  const running = useMemo(() => (status.envs ?? []).filter((e): e is EnvReleaseStatus & { release: PinnedRelease } => !!e.release && e.release.branchName === branch), [status.envs, branch]);
  const latest = latestQ.data ?? null;
  const froms = useMemo(() => Array.from(new Set([...running.map((e) => e.release.commit), ...(latest ? [latest.commit] : [])])).sort(), [running, latest]);

  // the counts: one compare per (release commit, head), cached under its own key so a pin change does not re-read it
  const changedQ = useQuery(
    branch && h && froms.length ? `${base}:changed:${branch}:${h}:${froms.join(',')}` : null,
    async () => {
      const out: Record<string, ChangeCount | null> = {};
      await Promise.all(
        froms.map(async (from) => {
          if (from === h) {
            out[from] = ZERO;
            return;
          }
          try {
            out[from] = await queryCache.get(`game:${org}/${game.slug}:compare:${from}..${h}`, async () => {
              const r = (await client.data(ops.compareCommits, { path: { org, game: game.slug, branch: branch! }, query: { from, to: h! } })) as { changes?: TreeChange[] };
              return countChangedDocuments(r.changes);
            });
          } catch {
            out[from] = null;
          }
        }),
      );
      return out;
    },
    { head: h },
  );

  return useMemo<BranchReleaseStatus>(() => {
    const changed = changedQ.data ?? {};
    const pinnedIds = new Set((status.envs ?? []).map((e) => e.release?.releaseId).filter(Boolean));
    return {
      loading: status.loading || (latestQ.loading && latestQ.data === undefined),
      forbidden: status.forbidden || (isForbidden(latestQ.error) && running.length === 0),
      head: h,
      environments: running.map((e) => ({ ...e, changed: changed[e.release.commit] ?? null })),
      latest: latest ? { release: latest, pinned: pinnedIds.has(latest.releaseId), changed: changed[latest.commit] ?? null } : null,
    };
  }, [changedQ.data, status.envs, status.loading, status.forbidden, latestQ.loading, latestQ.data, latestQ.error, running, latest, h]);
}
